import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, ChevronLeft, ChevronRight, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { EmailTestModeBadge } from '../../components/revenue/EmailTestModeBadge';
import { useRevenueAccess } from '../../hooks/useRevenueAccess';

/** PostgREST returns at most 1000 rows per request unless configured otherwise */
const DB_FETCH_BATCH = 1000;
/** Hotels shown under the search picker */
const HOTEL_PICK_PREVIEW = 40;

interface HotelRow {
  id: number;
  title: string | null;
}

interface ContactRow {
  id: string;
  hotel_id: number | null;
  client_id: number | null;
  contact_type: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  updated_at: string;
}

type DraftFields = {
  first_name: string;
  last_name: string;
  email: string;
};

function emptyDraft(): DraftFields {
  return { first_name: '', last_name: '', email: '' };
}

export default function HotelContacts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canMutateRevenue } = useRevenueAccess();
  const [hotels, setHotels] = useState<HotelRow[]>([]);
  const [hotelsLoading, setHotelsLoading] = useState(true);
  const [hotelSearch, setHotelSearch] = useState('');
  const [pickerPage, setPickerPage] = useState(1);
  const [selectedHotelId, setSelectedHotelId] = useState<number | null>(() => {
    const raw = searchParams.get('hotelId');
    const n = raw === null ? Number.NaN : Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  });

  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalMode, setContactModalMode] = useState<'add' | 'edit'>('add');
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [modalDraft, setModalDraft] = useState<DraftFields>(() => emptyDraft());
  const [modalSaving, setModalSaving] = useState(false);
  const [rowBusyId, setRowBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  /** Sync URL when hotel selection changes */
  const selectHotel = useCallback(
    (id: number | null) => {
      setSelectedHotelId(id);
      setPickerPage(1);
      setHotelSearch('');
      setContacts([]);
      setContactModalOpen(false);
      setEditingContactId(null);
      setModalDraft(emptyDraft());
      setSearchParams(id != null ? { hotelId: String(id) } : {});
    },
    [setSearchParams],
  );

  /** If URL gains hotelId externally, apply it */
  useEffect(() => {
    const raw = searchParams.get('hotelId');
    const n = raw === null ? Number.NaN : Number.parseInt(raw, 10);
    const nextId = Number.isFinite(n) && n > 0 ? n : null;
    setSelectedHotelId((cur) => (cur === nextId ? cur : nextId));
  }, [searchParams]);

  const loadHotels = useCallback(async () => {
    setHotelsLoading(true);
    setError(null);
    try {
      const list: HotelRow[] = [];
      let offset = 0;
      for (;;) {
        const { data: batch, error: hErr } = await supabase
          .from('hotels')
          .select('id, title')
          .order('title', { ascending: true })
          .range(offset, offset + DB_FETCH_BATCH - 1);
        if (hErr) throw hErr;
        const rows = (batch ?? []) as HotelRow[];
        list.push(...rows);
        if (rows.length === 0 || rows.length < DB_FETCH_BATCH) break;
        offset += DB_FETCH_BATCH;
      }
      setHotels(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load hotels');
    } finally {
      setHotelsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHotels();
  }, [loadHotels]);

  const loadContacts = useCallback(async (hotelId: number) => {
    setContactsLoading(true);
    setError(null);
    try {
      const { data, error: cErr } = await supabase
        .from('contacts')
        .select(
          'id, hotel_id, client_id, contact_type, first_name, last_name, email, created_at, updated_at',
        )
        .eq('hotel_id', hotelId)
        .eq('contact_type', 'invoice')
        .order('last_name', { ascending: true })
        .order('first_name', { ascending: true });
      if (cErr) throw cErr;
      const rows = (data ?? []) as ContactRow[];
      setContacts(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load contacts');
    } finally {
      setContactsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedHotelId != null) {
      void loadContacts(selectedHotelId);
    } else {
      setContacts([]);
    }
  }, [selectedHotelId, loadContacts]);

  const selectedHotelTitle = useMemo(() => {
    if (selectedHotelId == null) return null;
    const h = hotels.find((x) => x.id === selectedHotelId);
    return h?.title ?? `Hotel #${selectedHotelId}`;
  }, [hotels, selectedHotelId]);

  const filteredPickHotels = useMemo(() => {
    const q = hotelSearch.trim().toLowerCase();
    if (!q) return hotels;
    return hotels.filter((h) => {
      const title = (h.title || '').toLowerCase();
      if (title.includes(q)) return true;
      return String(h.id).includes(q);
    });
  }, [hotels, hotelSearch]);

  const pickTotalPages = Math.max(1, Math.ceil(filteredPickHotels.length / HOTEL_PICK_PREVIEW));
  useEffect(() => {
    setPickerPage(1);
  }, [hotelSearch]);
  useEffect(() => {
    if (pickerPage > pickTotalPages) setPickerPage(pickTotalPages);
  }, [pickerPage, pickTotalPages]);

  const paginatedPick = useMemo(() => {
    const start = (pickerPage - 1) * HOTEL_PICK_PREVIEW;
    return filteredPickHotels.slice(start, start + HOTEL_PICK_PREVIEW);
  }, [filteredPickHotels, pickerPage]);

  const validateDraft = (d: DraftFields): string | null => {
    if (!String(d.email ?? '').trim()) return 'Email is required.';
    return null;
  };

  const closeContactModal = useCallback(() => {
    setContactModalOpen(false);
    setEditingContactId(null);
    setModalDraft(emptyDraft());
  }, []);

  useEffect(() => {
    if (!contactModalOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !modalSaving) {
        closeContactModal();
      }
    };
    globalThis.addEventListener('keydown', onKeyDown);
    return () => globalThis.removeEventListener('keydown', onKeyDown);
  }, [contactModalOpen, modalSaving, closeContactModal]);

  const openAddContactModal = useCallback(() => {
    setContactModalMode('add');
    setEditingContactId(null);
    setModalDraft(emptyDraft());
    setContactModalOpen(true);
    setError(null);
    setHint(null);
  }, []);

  const openEditContactModal = useCallback((c: ContactRow) => {
    setContactModalMode('edit');
    setEditingContactId(c.id);
    setModalDraft({
      first_name: c.first_name ?? '',
      last_name: c.last_name ?? '',
      email: c.email ?? '',
    });
    setContactModalOpen(true);
    setError(null);
    setHint(null);
  }, []);

  const saveContactModal = async () => {
    if (!canMutateRevenue) return;
    const v = validateDraft(modalDraft);
    if (v) {
      setError(v);
      return;
    }
    if (selectedHotelId === null) return;
    if (contactModalMode === 'edit' && editingContactId === null) return;

    setModalSaving(true);
    setError(null);
    setHint(null);
    try {
      const now = new Date().toISOString();
      const payload = {
        first_name: modalDraft.first_name.trim(),
        last_name: modalDraft.last_name.trim(),
        email: modalDraft.email.trim(),
        updated_at: now,
      };

      if (contactModalMode === 'add') {
        const { error: insErr } = await supabase.from('contacts').insert({
          hotel_id: selectedHotelId,
          contact_type: 'invoice',
          ...payload,
        });
        if (insErr) throw insErr;
        setHint('Contact added.');
      } else if (editingContactId != null) {
        const { error: uErr } = await supabase
          .from('contacts')
          .update(payload)
          .eq('id', editingContactId);
        if (uErr) throw uErr;
        setHint('Contact updated.');
      }

      closeContactModal();
      void loadContacts(selectedHotelId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setModalSaving(false);
    }
  };

  const deleteRow = async (id: string) => {
    if (!canMutateRevenue) return;
    if (!globalThis.confirm('Remove this invoice contact from the hotel?')) return;
    setRowBusyId(id);
    setError(null);
    setHint(null);
    try {
      const { error: delErr } = await supabase.from('contacts').delete().eq('id', id);
      if (delErr) throw delErr;
      setHint('Contact removed.');
      if (selectedHotelId != null) void loadContacts(selectedHotelId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setRowBusyId(null);
    }
  };

  const hotelInList =
    selectedHotelId != null && hotels.some((h) => h.id === selectedHotelId);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="mx-auto max-w-[90rem] py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <Link to="/revenue-collection" className="btn-icon-back" aria-label="Back">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <h1 className="page-title">Hotel invoice contacts</h1>
            <EmailTestModeBadge />
          </div>
        </div>

        <p className="mb-4 max-w-3xl text-sm text-gray-600">
          {canMutateRevenue
            ? 'Manage invoice-type contacts per hotel. This is separate from the Backyou-synced hotels.invoicing_contact field.'
            : 'Invoice-type contacts per hotel. This is separate from the Backyou-synced hotels.invoicing_contact field.'}
        </p>

        {error && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}
        {hint && !error && (
          <div className="mb-4 rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {hint}
          </div>
        )}

        {hotelsLoading ? (
          <div className="text-gray-500">Loading hotels…</div>
        ) : (
          <div className="space-y-6">
            {!selectedHotelId || !hotelInList ? (
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <h2 className="mb-3 text-sm font-semibold text-gray-800">Choose a hotel</h2>
                <div className="relative max-w-md">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    aria-hidden
                  />
                  <input
                    type="search"
                    value={hotelSearch}
                    onChange={(e) => setHotelSearch(e.target.value)}
                    placeholder="Search by name or ID…"
                    className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-action focus:outline-none focus:ring-1 focus:ring-action/30"
                    aria-label="Search hotels"
                  />
                </div>
                {searchParams.get('hotelId') && !hotelInList && (
                  <p className="mt-2 text-sm text-amber-700">
                    Unknown hotel ID in URL. Pick a hotel below or clear the link.
                  </p>
                )}
                <ul className="mt-3 max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                  {paginatedPick.length === 0 ? (
                    <li className="px-3 py-4 text-center text-sm text-gray-500">No matches.</li>
                  ) : (
                    paginatedPick.map((h) => (
                      <li key={h.id}>
                        <button
                          type="button"
                          onClick={() => selectHotel(h.id)}
                          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-gray-900 hover:bg-[#148D82]/[0.06]"
                        >
                          <span className="min-w-0 truncate">{h.title || `Hotel #${h.id}`}</span>
                          <span className="shrink-0 tabular-nums text-xs text-gray-500">#{h.id}</span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
                {filteredPickHotels.length > 0 && (
                  <div className="mt-2 flex flex-col gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-gray-500 tabular-nums">
                      {filteredPickHotels.length} hotel{filteredPickHotels.length === 1 ? '' : 's'}
                      {filteredPickHotels.length > HOTEL_PICK_PREVIEW
                        ? ` · page ${pickerPage} of ${pickTotalPages}`
                        : ''}
                    </p>
                    {filteredPickHotels.length > HOTEL_PICK_PREVIEW && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setPickerPage((p) => Math.max(1, p - 1))}
                          disabled={pickerPage <= 1}
                          className="btn-secondary-sm"
                          aria-label="Previous hotels page"
                        >
                          <ChevronLeft className="h-4 w-4" aria-hidden />
                          Previous
                        </button>
                        <button
                          type="button"
                          onClick={() => setPickerPage((p) => Math.min(pickTotalPages, p + 1))}
                          disabled={pickerPage >= pickTotalPages}
                          className="btn-secondary-sm"
                          aria-label="Next hotels page"
                        >
                          Next
                          <ChevronRight className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Selected hotel</p>
                    <p className="truncate text-sm font-semibold text-gray-900">{selectedHotelTitle}</p>
                    <p className="text-xs text-gray-500 tabular-nums">ID {selectedHotelId}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn-secondary-sm"
                      onClick={() => selectHotel(null)}
                    >
                      Change hotel
                    </button>
                  </div>
                </div>

                {contactsLoading ? (
                  <div className="text-gray-500">Loading contacts…</div>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h2 className="text-sm font-semibold text-gray-800">Invoice contacts</h2>
                      {canMutateRevenue ? (
                        <button
                          type="button"
                          className="btn-primary inline-flex items-center gap-2"
                          onClick={openAddContactModal}
                        >
                          <Plus className="h-4 w-4 shrink-0" aria-hidden />
                          Add a contact
                        </button>
                      ) : null}
                    </div>

                    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                              First name
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                              Last name
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                              Email
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                              Contact type
                            </th>
                            {canMutateRevenue ? (
                              <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                                Actions
                              </th>
                            ) : null}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {contacts.length === 0 ? (
                            <tr>
                              <td colSpan={canMutateRevenue ? 5 : 4} className="px-4 py-6 text-center text-gray-500">
                                {canMutateRevenue
                                  ? 'No invoice contacts yet. Use "Add a contact" to create one.'
                                  : 'No invoice contacts yet.'}
                              </td>
                            </tr>
                          ) : (
                            contacts.map((c) => {
                              const busy = rowBusyId === c.id;
                              return (
                                <tr key={c.id} className="align-middle">
                                  <td className="px-4 py-2 text-gray-900">
                                    {c.first_name?.trim() ? c.first_name : '—'}
                                  </td>
                                  <td className="px-4 py-2 text-gray-900">
                                    {c.last_name?.trim() ? c.last_name : '—'}
                                  </td>
                                  <td className="px-4 py-2 text-gray-900">
                                    <span className="break-all">{c.email}</span>
                                  </td>
                                  <td className="px-4 py-2 text-gray-800 capitalize">{c.contact_type}</td>
                                  {canMutateRevenue ? (
                                    <td className="whitespace-nowrap px-4 py-2 text-right">
                                      <button
                                        type="button"
                                        onClick={() => openEditContactModal(c)}
                                        disabled={busy || contactModalOpen || modalSaving}
                                        className="btn-secondary-sm mr-1 inline-flex items-center gap-1"
                                      >
                                        <Pencil className="h-4 w-4 shrink-0" aria-hidden />
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => void deleteRow(c.id)}
                                        disabled={busy}
                                        className="btn-secondary-sm inline-flex items-center gap-1 !border-red-200/90 bg-white text-red-700 shadow-sm hover:!border-red-300/90 hover:bg-red-50 hover:text-red-800 focus-visible:ring-red-400/35 disabled:opacity-50"
                                        aria-label="Remove contact"
                                      >
                                        <Trash2 className="h-4 w-4 shrink-0" aria-hidden />
                                      </button>
                                    </td>
                                  ) : null}
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {canMutateRevenue && contactModalOpen && (
                      <div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                        role="presentation"
                      >
                        <button
                          type="button"
                          className="absolute inset-0 bg-black/50"
                          aria-label="Close dialog"
                          disabled={modalSaving}
                          onClick={closeContactModal}
                        />
                        <div
                          className="relative z-10 w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="contact-modal-title"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 pr-12">
                            <h2 id="contact-modal-title" className="text-base font-semibold text-gray-900">
                              {contactModalMode === 'add' ? 'Add invoice contact' : 'Edit invoice contact'}
                            </h2>
                            <button
                              type="button"
                              onClick={closeContactModal}
                              disabled={modalSaving}
                              className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                              aria-label="Close"
                            >
                              <X className="h-5 w-5" aria-hidden />
                            </button>
                          </div>
                          <div className="space-y-4 p-4">
                            <div>
                              <label
                                htmlFor="modal-contact-first-name"
                                className="mb-1 block text-xs font-medium text-gray-600"
                              >
                                First name
                              </label>
                              <input
                                id="modal-contact-first-name"
                                type="text"
                                value={modalDraft.first_name}
                                onChange={(e) =>
                                  setModalDraft((prev) => ({ ...prev, first_name: e.target.value }))
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                                disabled={modalSaving}
                              />
                            </div>
                            <div>
                              <label
                                htmlFor="modal-contact-last-name"
                                className="mb-1 block text-xs font-medium text-gray-600"
                              >
                                Last name
                              </label>
                              <input
                                id="modal-contact-last-name"
                                type="text"
                                value={modalDraft.last_name}
                                onChange={(e) =>
                                  setModalDraft((prev) => ({ ...prev, last_name: e.target.value }))
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                                disabled={modalSaving}
                              />
                            </div>
                            <div>
                              <label
                                htmlFor="modal-contact-email"
                                className="mb-1 block text-xs font-medium text-gray-600"
                              >
                                Email
                              </label>
                              <input
                                id="modal-contact-email"
                                type="email"
                                value={modalDraft.email}
                                onChange={(e) =>
                                  setModalDraft((prev) => ({ ...prev, email: e.target.value }))
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                                disabled={modalSaving}
                              />
                            </div>
                            <div className="flex flex-wrap justify-end gap-2 pt-1">
                              <button
                                type="button"
                                className="btn-secondary"
                                disabled={modalSaving}
                                onClick={closeContactModal}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="btn-primary"
                                disabled={modalSaving}
                                onClick={() => void saveContactModal()}
                              >
                                {modalSaving ? 'Saving…' : 'Save'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
